import React, { useState, useEffect, useMemo, useRef } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, Plus, CheckCircle, AlertCircle, Clock } from "lucide-react";
import { Button } from "@core/button";
import { Text } from "@core/text";
import { useToast } from "@packages/contexts/ToastContext";
import { CourseData, isTurkishCourse } from "@packages/constant/CourseData";
import { UnitOutcomeSelector } from "@features/assignments/AssignmentGenerator/components/unit-outcome-selector";
import GradeSelector from "@features/tools/components/grade-selector";
import CourseSelector from "@features/assignments/AssignmentGenerator/components/course-selector";
import LoadingContainer from "@packages/components/Loading";
import ErrorComponent from "@features/tools/components/tool_components/ErrorComponent";
import {
  GetTopicsCourseType,
  LessonHookCreateRequestDtoCourseName,
  LessonHookCreateRequestDtoGenerationType,
  CommonMisconceptionCreateRequestDtoCourseName,
  CommonMisconceptionCreateRequestDtoGenerationType,
  TedLessonPlannerCreateRequestDto,
  TedLessonPlannerCreateRequestDtoCourseName,
  TedLessonPlannerInputMaterials,
  TedLessonPlannerInputMaterialsToolType,
} from "@packages/api/madlenAPI.schemas";
import {
  createLessonHook,
  createCommonMisconception,
  useGetLessonHook,
  useGetCommonMisconception,
  useCreateTedLessonPlanner,
  useGetTedLessonPlanner,
} from "@packages/api/madlenAPI";
import { getEnvironment } from "@packages/utils";

interface CreatedMaterial {
  toolType: TedLessonPlannerInputMaterialsToolType;
  id: number;
  title: string;
  isDataReady?: boolean;
}

interface PollingState {
  lessonHookId: number | null;
  misconceptionId: number | null;
  tedPlanId: number | null;
  lessonHookReady: boolean;
  misconceptionReady: boolean;
  tedPlanReady: boolean;
}

const CreateTedPlanView: React.FC = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const { addToast } = useToast();
  const createTedPlan = useCreateTedLessonPlanner();

  // Form state
  const [selectedGrade, setSelectedGrade] = useState<number>(5);
  const [selectedCourse, setSelectedCourse] =
    useState<GetTopicsCourseType>("HISTORY");
  const [selectedOutcomes, setSelectedOutcomes] = useState<number[]>([]);
  const [isMaarif, setIsMaarif] = useState(true);

  // Loading and progress state
  const [isGenerating, setIsGenerating] = useState(false);
  const [currentStep, setCurrentStep] = useState(0);
  const [completedSteps, setCompletedSteps] = useState<boolean[]>([]);
  const [createdMaterials, setCreatedMaterials] = useState<CreatedMaterial[]>(
    [],
  );
  const [error, setError] = useState<string | null>(null);

  // Polling state
  const [pollingState, setPollingState] = useState<PollingState>({
    lessonHookId: null,
    misconceptionId: null,
    tedPlanId: null,
    lessonHookReady: false,
    misconceptionReady: false,
    tedPlanReady: false,
  });

  // Polling timeout refs
  const pollingTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  // Hook for polling lesson hook data
  const { data: lessonHookData } = useGetLessonHook(
    pollingState.lessonHookId!,
    {
      query: {
        enabled: !!pollingState.lessonHookId && !pollingState.lessonHookReady,
        refetchInterval: (query) => {
          const hooks = query?.state?.data?.hooks;
          if (hooks && Array.isArray(hooks) && hooks.length > 0) {
            return false; // Stop polling when data is ready
          }
          return 3000; // Poll every 3 seconds
        },
        refetchOnWindowFocus: false,
      },
    },
  );

  // Hook for polling common misconception data
  const { data: misconceptionData } = useGetCommonMisconception(
    pollingState.misconceptionId!,
    {
      query: {
        enabled:
          !!pollingState.misconceptionId && !pollingState.misconceptionReady,
        refetchInterval: (query) => {
          const misconceptions = query?.state?.data?.misconceptions;
          if (
            misconceptions &&
            Array.isArray(misconceptions) &&
            misconceptions.length > 0
          ) {
            return false; // Stop polling when data is ready
          }
          return 3000; // Poll every 3 seconds
        },
        refetchOnWindowFocus: false,
      },
    },
  );

  // Hook for polling Ted plan data
  const { data: tedPlanData } = useGetTedLessonPlanner(
    pollingState.tedPlanId!,
    {
      query: {
        enabled: !!pollingState.tedPlanId && !pollingState.tedPlanReady,
        refetchInterval: (query) => {
          const steps = query?.state?.data?.steps;
          if (steps && Array.isArray(steps) && steps.length > 0) {
            return false; // Stop polling when data is ready
          }
          return 3000; // Poll every 3 seconds
        },
        refetchOnWindowFocus: false,
      },
    },
  );

  // Auto-detect Maarif based on grade
  useEffect(() => {
    if (selectedGrade < 5) {
      setIsMaarif(true);
    }
  }, [selectedGrade]);

  // Define loading steps
  const steps = useMemo(() => {
    if (i18n.language === "tr") {
      return [
        "Ders kancası oluşturuluyor...",
        "Yaygın kavram yanılgıları belirleniyor...",
        "Materyaller hazırlanıyor...",
        "Ek materyaller analiz ediliyor...",
        "Ted planı oluşturuluyor...",
        "Son kontroller yapılıyor...",
      ];
    }
    if (i18n.language === "fr") {
      return [
        "Création de l'accroche de cours...",
        "Identification des idées fausses communes...",
        "Préparation des matériaux...",
        "Analyse des matériaux supplémentaires...",
        "Création du plan Ted...",
        "Finalisation du processus...",
      ];
    }
    if (i18n.language === "es") {
      return [
        "Creando la accroche de la lección...",
        "Identificando ideas falsas comunes...",
        "Preparando materiales...",
        "Analizando materiales adicionales...",
        "Creando plan Ted...",
        "Finalizando proceso...",
      ];
    }
    if (i18n.language === "de") {
      return [
        "Unterrichtshaken wird erstellt...",
        "Häufige Missverständnisse werden identifiziert...",
        "Materialien werden vorbereitet...",
        "Zusätzliche Materialien werden analysiert...",
        "Ted-Plan wird erstellt...",
        "Abschließende Überprüfungen...",
      ];
    }
    return [
      "Creating lesson hook...",
      "Identifying common misconceptions...",
      "Preparing materials...",
      "Analyzing additional materials...",
      "Generating Ted plan...",
      "Finalizing process...",
    ];
  }, [i18n.language]);

  // Check if lesson hook data is ready
  useEffect(() => {
    if (
      lessonHookData?.hooks &&
      Array.isArray(lessonHookData.hooks) &&
      lessonHookData.hooks.length > 0
    ) {
      setPollingState((prev) => ({ ...prev, lessonHookReady: true }));
      setCreatedMaterials((prev) =>
        prev.map((material) =>
          material.toolType === "LESSON_HOOK"
            ? { ...material, isDataReady: true }
            : material,
        ),
      );
    }
  }, [lessonHookData]);

  // Check if common misconception data is ready
  useEffect(() => {
    if (
      misconceptionData?.misconceptions &&
      Array.isArray(misconceptionData.misconceptions) &&
      misconceptionData.misconceptions.length > 0
    ) {
      setPollingState((prev) => ({ ...prev, misconceptionReady: true }));
      setCreatedMaterials((prev) =>
        prev.map((material) =>
          material.toolType === "COMMON_MISCONCEPTION"
            ? { ...material, isDataReady: true }
            : material,
        ),
      );
    }
  }, [misconceptionData]);

  // Check if Ted plan data is ready
  useEffect(() => {
    if (
      tedPlanData?.steps &&
      Array.isArray(tedPlanData.steps) &&
      tedPlanData.steps.length > 0
    ) {
      setPollingState((prev) => ({ ...prev, tedPlanReady: true }));
    }
  }, [tedPlanData]);

  // Continue with Ted plan creation when both materials are ready
  useEffect(() => {
    if (
      isGenerating &&
      pollingState.lessonHookReady &&
      pollingState.misconceptionReady &&
      currentStep === 2
    ) {
      // Both materials are ready, continue with Ted plan creation
      continueWithTedPlanCreation();
    }
  }, [
    pollingState.lessonHookReady,
    pollingState.misconceptionReady,
    isGenerating,
    currentStep,
  ]);

  // Complete the process when Ted plan is ready
  useEffect(() => {
    if (isGenerating && pollingState.tedPlanReady && currentStep === 4) {
      // Ted plan is ready, complete the process
      finalizeTedPlanCreation();
    }
  }, [pollingState.tedPlanReady, isGenerating, currentStep]);

  // Cleanup polling timeout on unmount
  useEffect(() => {
    return () => {
      if (pollingTimeoutRef.current) {
        clearTimeout(pollingTimeoutRef.current);
      }
    };
  }, []);

  const handleGradeChange = (grade: number) => {
    const availableCourses = Object.entries(CourseData).filter(
      ([, value]) => value.visible(grade) && isTurkishCourse(value.name(grade)),
    );
    const firstAvailableCourse =
      availableCourses[0]?.[0] as GetTopicsCourseType;
    const doesPreviousCourseExist = availableCourses.some(
      ([key]) => key === selectedCourse,
    );

    setSelectedGrade(grade);
    if (!doesPreviousCourseExist) {
      setSelectedCourse(firstAvailableCourse);
    }
    setSelectedOutcomes([]);
  };

  const handleCourseChange = (course: GetTopicsCourseType) => {
    setSelectedCourse(course);
    setSelectedOutcomes([]);
  };

  const createLessonHookMaterial = async () => {
    try {
      const response = await createLessonHook({
        courseName: selectedCourse as LessonHookCreateRequestDtoCourseName,
        gradeLevel: selectedGrade,
        learningOutcomeIds: selectedOutcomes,
        generationType:
          LessonHookCreateRequestDtoGenerationType.MAARIF_LEARNING_OUTCOME,
        environment: getEnvironment(),
        contentLanguage: i18n.language as any,
        isPublic: false,
        fromSuperTool: false,
        prompt: "",
      });

      const material = {
        toolType: "LESSON_HOOK" as const,
        id: response.id!,
        title: response.title || t("tedPlan.materials.lessonHook"),
        isDataReady: false,
      };

      // Start polling for lesson hook data
      setPollingState((prev) => ({ ...prev, lessonHookId: response.id! }));

      return material;
    } catch (error) {
      console.error("Error creating lesson hook:", error);
      throw new Error(t("tedPlan.create.error.lessonHookFailed"));
    }
  };

  const createCommonMisconceptionMaterial = async () => {
    try {
      const response = await createCommonMisconception({
        courseName:
          selectedCourse as CommonMisconceptionCreateRequestDtoCourseName,
        gradeLevel: selectedGrade,
        learningOutcomeIds: selectedOutcomes,
        generationType:
          CommonMisconceptionCreateRequestDtoGenerationType.MAARIF_LEARNING_OUTCOME,
        environment: getEnvironment(),
        contentLanguage: i18n.language as any,
        isPublic: false,
        fromSuperTool: false,
        prompt: "",
      });

      const material = {
        toolType: "COMMON_MISCONCEPTION" as const,
        id: response.id!,
        title: response.title || t("tedPlan.materials.commonMisconception"),
        isDataReady: false,
      };

      // Start polling for common misconception data
      setPollingState((prev) => ({ ...prev, misconceptionId: response.id! }));

      return material;
    } catch (error) {
      console.error("Error creating common misconception:", error);
      throw new Error(t("tedPlan.create.error.misconceptionFailed"));
    }
  };

  const simulateStepProgress = (stepIndex: number, duration: number) => {
    return new Promise((resolve) => {
      setTimeout(() => {
        setCompletedSteps((prev) => {
          const newCompleted = [...prev];
          newCompleted[stepIndex] = true;
          return newCompleted;
        });
        resolve(void 0);
      }, duration);
    });
  };

  const continueWithTedPlanCreation = async () => {
    try {
      // Step 3: Materials are ready (complete this step)
      await simulateStepProgress(2, 500);

      // Step 4: Analyze Materials (simulated)
      setCurrentStep(3);
      await simulateStepProgress(3, 1000);

      // Step 5: Create Ted Plan
      setCurrentStep(4);
      const additionalMaterials: TedLessonPlannerInputMaterials[] = [
        {
          toolType: TedLessonPlannerInputMaterialsToolType.LESSON_HOOK,
          id: pollingState.lessonHookId!,
        },
        {
          toolType: TedLessonPlannerInputMaterialsToolType.COMMON_MISCONCEPTION,
          id: pollingState.misconceptionId!,
        },
      ];

      const payload: TedLessonPlannerCreateRequestDto = {
        grade: selectedGrade,
        courseName:
          selectedCourse as TedLessonPlannerCreateRequestDtoCourseName,
        maarifLearningOutcomeIds: selectedOutcomes,
        additionalMaterials,
      };

      const newTedPlan = await createTedPlan.mutateAsync({ data: payload });

      // Start polling for Ted plan completion
      setPollingState((prev) => ({ ...prev, tedPlanId: newTedPlan.id! }));

      // Note: finalizeTedPlanCreation will be called automatically when Ted plan is ready
    } catch (error: any) {
      console.error("Error creating Ted plan:", error);
      setError(error.message || t("tedPlan.create.error.description"));

      addToast({
        title: t("tedPlan.create.error.title"),
        description: error.message || t("tedPlan.create.error.description"),
        variant: "error",
        duration: 5000,
      });

      setIsGenerating(false);
    }
  };

  const finalizeTedPlanCreation = async () => {
    try {
      // Step 5: Ted plan creation completed
      await simulateStepProgress(4, 800);

      // Step 6: Finalize
      setCurrentStep(5);
      await simulateStepProgress(5, 500);

      addToast({
        title: t("tedPlan.create.success.title"),
        description: t("tedPlan.create.success.description"),
        variant: "success",
        duration: 3000,
      });

      // Navigate to the created Ted plan
      navigate(`/lesson-planning-tools/ted-plan/${pollingState.tedPlanId}`);
    } catch (error: any) {
      console.error("Error finalizing Ted plan:", error);
      setError(error.message || t("tedPlan.create.error.description"));

      addToast({
        title: t("tedPlan.create.error.title"),
        description: error.message || t("tedPlan.create.error.description"),
        variant: "error",
        duration: 5000,
      });
    } finally {
      setIsGenerating(false);
    }
  };

  const handleSubmit = async () => {
    if (selectedOutcomes.length === 0) {
      addToast({
        title: t("tedPlan.create.validation.outcomesRequired"),
        description: t("tedPlan.create.validation.outcomesRequiredDesc"),
        variant: "error",
        duration: 3000,
      });
      return;
    }

    setIsGenerating(true);
    setCurrentStep(0);
    setCompletedSteps(new Array(steps.length).fill(false));
    setCreatedMaterials([]);
    setError(null);
    setPollingState({
      lessonHookId: null,
      misconceptionId: null,
      tedPlanId: null,
      lessonHookReady: false,
      misconceptionReady: false,
      tedPlanReady: false,
    });

    try {
      // Step 1: Create Lesson Hook
      setCurrentStep(0);
      const lessonHook = await createLessonHookMaterial();
      await simulateStepProgress(0, 800);
      setCreatedMaterials((prev) => [...prev, lessonHook]);

      // Step 2: Create Common Misconception
      setCurrentStep(1);
      const commonMisconception = await createCommonMisconceptionMaterial();
      await simulateStepProgress(1, 800);
      setCreatedMaterials((prev) => [...prev, commonMisconception]);

      // Step 3: Wait for materials to be ready (polling will handle the rest)
      setCurrentStep(2);
      // Note: continueWithTedPlanCreation will be called automatically when both materials are ready
    } catch (error: any) {
      console.error("Error in initial creation phase:", error);
      setError(error.message || t("tedPlan.create.error.description"));

      addToast({
        title: t("tedPlan.create.error.title"),
        description: error.message || t("tedPlan.create.error.description"),
        variant: "error",
        duration: 5000,
      });

      setIsGenerating(false);
    }
  };

  if (isGenerating) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex items-center justify-center">
        <div className="max-w-2xl w-full px-4">
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-lg p-8">
            <div className="text-center mb-8">
              <Text
                variant="h2"
                className="text-2xl font-bold text-orange-600 dark:text-orange-400 mb-2"
              >
                {t("tedPlan.create.generating")}
              </Text>
              <Text variant="p" className="text-gray-600 dark:text-gray-400">
                {t("tedPlan.create.generatingDesc")}
              </Text>
            </div>

            <div className="space-y-6">
              {steps.map((step, index) => {
                const isPollingStep = index === 2;
                const isWaitingForMaterials =
                  currentStep === 2 &&
                  (!pollingState.lessonHookReady ||
                    !pollingState.misconceptionReady);

                return (
                  <div key={index} className="flex items-center space-x-4">
                    <div
                      className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 transition-all duration-300 ${
                        completedSteps[index]
                          ? "bg-green-500 text-white"
                          : currentStep === index
                            ? isPollingStep && isWaitingForMaterials
                              ? "bg-amber-500 text-white"
                              : "bg-orange-500 text-white animate-pulse"
                            : currentStep > index
                              ? "bg-orange-200 dark:bg-orange-900 text-orange-600 dark:text-orange-400"
                              : "bg-gray-200 dark:bg-gray-700 text-gray-400"
                      }`}
                    >
                      {completedSteps[index] ? (
                        <CheckCircle className="w-5 h-5" />
                      ) : isPollingStep && isWaitingForMaterials ? (
                        <Clock className="w-5 h-5 animate-pulse" />
                      ) : (
                        <span className="text-sm font-bold">{index + 1}</span>
                      )}
                    </div>

                    <div className="flex-1">
                      <Text
                        variant="p"
                        className={`font-medium transition-colors duration-300 ${
                          completedSteps[index]
                            ? "text-green-600 dark:text-green-400"
                            : currentStep === index
                              ? isPollingStep && isWaitingForMaterials
                                ? "text-amber-600 dark:text-amber-400"
                                : "text-orange-600 dark:text-orange-400"
                              : "text-gray-500 dark:text-gray-500"
                        }`}
                      >
                        {step}
                      </Text>

                      {/* Show material status */}
                      {index < createdMaterials.length && (
                        <div className="mt-1 space-y-1">
                          {createdMaterials
                            .filter(
                              (_, materialIndex) => materialIndex === index,
                            )
                            .map((material, materialIndex) => (
                              <Text
                                key={materialIndex}
                                variant="p"
                                size="sm"
                                className={`${
                                  material.isDataReady
                                    ? "text-green-600 dark:text-green-400"
                                    : "text-amber-600 dark:text-amber-400"
                                }`}
                              >
                                {material.isDataReady ? "✓" : "⏳"}{" "}
                                {material.title}
                                {!material.isDataReady &&
                                  currentStep >= index &&
                                  ` (${t("tedPlan.materials.preparing")})`}
                              </Text>
                            ))}
                        </div>
                      )}

                      {/* Show polling status for step 3 */}
                      {isPollingStep && currentStep === 2 && (
                        <div className="mt-1 space-y-1">
                          <Text
                            variant="p"
                            size="sm"
                            className={`${
                              pollingState.lessonHookReady
                                ? "text-green-600 dark:text-green-400"
                                : "text-amber-600 dark:text-amber-400"
                            }`}
                          >
                            {pollingState.lessonHookReady ? "✓" : "⏳"}{" "}
                            {t("tedPlan.materials.lessonHook")}
                          </Text>
                          <Text
                            variant="p"
                            size="sm"
                            className={`${
                              pollingState.misconceptionReady
                                ? "text-green-600 dark:text-green-400"
                                : "text-amber-600 dark:text-amber-400"
                            }`}
                          >
                            {pollingState.misconceptionReady ? "✓" : "⏳"}{" "}
                            {t("tedPlan.materials.commonMisconception")}
                          </Text>
                        </div>
                      )}

                      {/* Show polling status for step 5 (Ted plan creation) */}
                      {index === 4 && currentStep === 4 && (
                        <div className="mt-1">
                          <Text
                            variant="p"
                            size="sm"
                            className={`${
                              pollingState.tedPlanReady
                                ? "text-green-600 dark:text-green-400"
                                : "text-amber-600 dark:text-amber-400"
                            }`}
                          >
                            {pollingState.tedPlanReady ? "✓" : "⏳"}{" "}
                            {t("tedPlan.materials.preparing")}
                          </Text>
                        </div>
                      )}
                    </div>

                    {currentStep === index && !completedSteps[index] && (
                      <div className="w-6 h-6 border-2 border-orange-500 border-t-transparent rounded-full animate-spin"></div>
                    )}
                  </div>
                );
              })}
            </div>

            {error && (
              <div className="mt-6 p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-700 rounded-lg flex items-start space-x-3">
                <AlertCircle className="w-5 h-5 text-red-500 mt-0.5 flex-shrink-0" />
                <Text
                  variant="p"
                  size="sm"
                  className="text-red-700 dark:text-red-400"
                >
                  {error}
                </Text>
              </div>
            )}
          </div>
        </div>
      </div>
    );
  }

  if (error && !isGenerating) {
    return (
      <ErrorComponent
        title={t("tedPlan.create.error.title")}
        description={error}
        retryButton={t("common.tryAgain")}
        refetch={() => {
          setError(null);
          handleSubmit();
        }}
      />
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
      <div className="max-w-4xl mx-auto py-8 px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="mb-8">
          <Button
            onClick={() => navigate("/lesson-planner")}
            variant="ghost"
            size="sm"
            className="mb-4 text-gray-600 hover:text-gray-800 dark:text-gray-400 dark:hover:text-gray-200"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            {t("tedPlan.backToPlans")}
          </Button>

          <div className="text-center">
            <Text
              variant="h1"
              className="text-3xl font-bold text-gray-900 dark:text-white mb-2"
            >
              {t("tedPlan.create.title")}
            </Text>
            <Text
              variant="p"
              className="text-gray-600 dark:text-gray-400 max-w-2xl mx-auto"
            >
              {t("tedPlan.create.description")}
            </Text>
          </div>
        </div>

        {/* Enhanced Features Info */}
        <div className="mb-8 bg-gradient-to-r from-orange-50 to-amber-50 dark:from-orange-900/20 dark:to-amber-900/20 border border-orange-200 dark:border-orange-700 rounded-lg p-6">
          <Text
            variant="h3"
            className="text-lg font-semibold text-orange-800 dark:text-orange-200 mb-3"
          >
            {t("tedPlan.create.enhancedFeatures")}
          </Text>
          <Text
            variant="p"
            size="sm"
            className="text-orange-700 dark:text-orange-300 mb-4"
          >
            {t("tedPlan.create.enhancedFeaturesDesc")}
          </Text>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="flex items-center space-x-3">
              <div className="w-8 h-8 bg-orange-500 rounded-full flex items-center justify-center">
                <CheckCircle className="w-5 h-5 text-white" />
              </div>
              <Text
                variant="p"
                size="sm"
                className="text-orange-800 dark:text-orange-200"
              >
                {t("tedPlan.materials.lessonHook")}
              </Text>
            </div>
            <div className="flex items-center space-x-3">
              <div className="w-8 h-8 bg-orange-500 rounded-full flex items-center justify-center">
                <CheckCircle className="w-5 h-5 text-white" />
              </div>
              <Text
                variant="p"
                size="sm"
                className="text-orange-800 dark:text-orange-200"
              >
                {t("tedPlan.materials.commonMisconception")}
              </Text>
            </div>
          </div>
        </div>

        {/* Form */}
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg overflow-hidden">
          <div className="p-6 space-y-8">
            {/* Basic Info Section */}
            <div>
              <Text
                variant="h3"
                className="text-lg font-semibold text-gray-900 dark:text-white mb-4"
              >
                {t("tedPlan.create.basicInfo")}
              </Text>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* Grade Selection */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">
                    {t("dailyLessonPlan.gradeLevel")} *
                  </label>
                  <div className="grid grid-cols-3 gap-2">
                    <GradeSelector
                      selectedGrade={selectedGrade}
                      setSelectedGrade={handleGradeChange}
                      minGrade={1}
                    />
                  </div>
                </div>

                {/* Course Selection */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">
                    {t("dailyLessonPlan.lesson")} *
                  </label>
                  <div className="grid grid-cols-2 gap-2">
                    <CourseSelector
                      selectedGrade={selectedGrade}
                      selectedCourse={selectedCourse}
                      filteredCourses={Object.entries(CourseData)
                        .filter(([key]) => !isTurkishCourse(key))
                        .map(([key]) => key as GetTopicsCourseType)}
                      setSelectedCourse={handleCourseChange}
                    />
                  </div>
                </div>
              </div>
            </div>

            {/* Learning Outcomes Section */}
            {selectedGrade && selectedCourse && (
              <div>
                <Text
                  variant="h3"
                  className="text-lg font-semibold text-gray-900 dark:text-white mb-4"
                >
                  {t("tedPlan.create.learningOutcomes")} *
                </Text>
                <Text
                  variant="p"
                  size="sm"
                  className="text-gray-600 dark:text-gray-400 mb-4"
                >
                  {t("tedPlan.create.learningOutcomesDesc")}
                </Text>

                <UnitOutcomeSelector
                  grade={selectedGrade}
                  courseType={selectedCourse}
                  selectedOutcomes={selectedOutcomes}
                  isMaarif={isMaarif}
                  setSelectedOutcomes={(outcomeIds) => {
                    setSelectedOutcomes(outcomeIds);
                  }}
                />
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="px-6 py-4 bg-gray-50 dark:bg-gray-700 border-t border-gray-200 dark:border-gray-600">
            <div className="flex justify-between items-center">
              <Button
                onClick={() => navigate("/lesson-planner")}
                disabled={isGenerating}
              >
                {t("common.cancel")}
              </Button>

              <Button
                onClick={handleSubmit}
                disabled={isGenerating || selectedOutcomes.length === 0}
                variant="solid"
                size="md"
                rounded="lg"
                bgColor="bg-orange-100 hover:bg-orange-200 dark:bg-orange-900/50 dark:hover:bg-orange-900/70"
                textColor="text-orange-600 dark:text-orange-400"
                className="group"
                leftIcon={<Plus className="w-4 h-4 mr-2" />}
              >
                {t("tedPlan.create.submit")}
              </Button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CreateTedPlanView;
